import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { getSessionId, setSessionId, clearSession } from "../lib/api";
import { MOOD_META } from "../lib/moods";

const fetchInto = async (path, setter, label) => {
  try {
    const r = await api.get(path);
    setter(r.data);
  } catch (e) {
    console.error(`Failed to load ${label}:`, e);
    toast.error(`Impossible de charger ${label}`);
  }
};

const consumeCallbackSession = () => {
  const sid = new URLSearchParams(window.location.search).get("session_id");
  if (sid) {
    setSessionId(sid);
    window.history.replaceState({}, "", "/");
  }
};

export default function useGlimpseSession() {
  const [session, setSession] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [moods, setMoods] = useState([]);
  const [library, setLibrary] = useState({ count: 0, synced_at: null });
  const [history, setHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [authError, setAuthError] = useState(null);

  const refreshLibrary = useCallback(() => fetchInto("/library/status", setLibrary, "ta bibliothèque"), []);
  const refreshHistory = useCallback(() => fetchInto("/history", setHistory, "l'historique"), []);
  const refreshFavorites = useCallback(() => fetchInto("/favorites", setFavorites, "tes favoris"), []);

  useEffect(() => {
    const bootstrap = async () => {
      consumeCallbackSession();
      if (!getSessionId()) return;
      try {
        const [me, m] = await Promise.all([api.get("/me"), api.get("/moods")]);
        setSession(me.data);
        setMoods(Object.keys(MOOD_META).map((k) => m.data.find((x) => x.key === k)).filter(Boolean));
        await Promise.all([refreshLibrary(), refreshHistory(), refreshFavorites()]);
      } catch (e) {
        console.error("Session bootstrap failed:", e);
        clearSession();
      }
    };
    bootstrap().finally(() => setBootstrapping(false));
  }, [refreshLibrary, refreshHistory, refreshFavorites]);

  const login = async () => {
    try {
      const r = await api.get("/auth/login");
      window.location.href = r.data.auth_url;
    } catch (e) {
      setAuthError(e.response?.data?.detail || "Spotify n'est pas configuré.");
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed:", e);
    }
    clearSession();
    setSession(null);
    setHistory([]);
    setFavorites([]);
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/library/sync");
      toast.success(`${r.data.count} titres synchronisés`);
      await refreshLibrary();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  const deleteFavorite = async (id) => {
    try {
      await api.delete(`/favorites/${id}`);
      setFavorites((f) => f.filter((x) => x.id !== id));
    } catch (e) {
      console.error("Delete favorite failed:", e);
      toast.error("Impossible de supprimer");
    }
  };

  return {
    session, bootstrapping, moods, library, history, favorites, syncing, authError,
    refreshHistory, refreshFavorites, login, logout, sync, deleteFavorite,
  };
}
